import React, { useState } from 'react';
import { Shield, CheckCircle, XCircle, AlertTriangle, ArrowRight, ArrowLeft, Download } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const SecurityRiskAssessment = ({ isOpen, onClose, onOpenChat }) => {
  const { t } = useTranslation();
  const [currentStep, setCurrentStep] = useState(0);
  const [answers, setAnswers] = useState({});
  const [showResults, setShowResults] = useState(false);

  const questions = [
    {
      id: 'mfa',
      category: 'Zugangssicherheit',
      question: 'Nutzen Sie Multi-Faktor-Authentifizierung (MFA) für alle kritischen Systeme?',
      options: [
        { value: 'yes', label: 'Ja, überall', risk: 0 },
        { value: 'partial', label: 'Teilweise', risk: 5 },
        { value: 'no', label: 'Nein', risk: 10 }
      ]
    },
    {
      id: 'backups',
      category: 'Datensicherung',
      question: 'Wie oft werden Ihre Backups getestet und validiert?',
      options: [
        { value: 'monthly', label: 'Monatlich oder häufiger', risk: 0 },
        { value: 'quarterly', label: 'Quartalsweise', risk: 3 },
        { value: 'yearly', label: 'Jährlich oder seltener', risk: 7 },
        { value: 'never', label: 'Nie / Nicht sicher', risk: 10 }
      ]
    },
    {
      id: 'patches',
      category: 'System-Updates',
      question: 'Wie schnell werden kritische Sicherheits-Patches eingespielt?',
      options: [
        { value: 'immediate', label: 'Innerhalb von 24-48 Stunden', risk: 0 },
        { value: 'week', label: 'Innerhalb einer Woche', risk: 4 },
        { value: 'month', label: 'Innerhalb eines Monats', risk: 8 },
        { value: 'irregular', label: 'Unregelmäßig', risk: 10 }
      ]
    },
    {
      id: 'drp',
      category: 'Notfallmanagement',
      question: 'Haben Sie einen dokumentierten und getesteten Disaster Recovery Plan?',
      options: [
        { value: 'tested', label: 'Ja, regelmäßig getestet', risk: 0 },
        { value: 'documented', label: 'Ja, aber nicht getestet', risk: 5 },
        { value: 'partial', label: 'Teilweise vorhanden', risk: 8 },
        { value: 'no', label: 'Nein', risk: 10 }
      ]
    },
    {
      id: 'endpoints',
      category: 'Endpoint-Schutz',
      question: 'Wie werden Ihre Endgeräte (PCs, Laptops, Smartphones) geschützt?',
      options: [
        { value: 'enterprise', label: 'Enterprise EDR/XDR-Lösung', risk: 0 },
        { value: 'standard', label: 'Standard-Antivirus', risk: 5 },
        { value: 'basic', label: 'Windows Defender / Basic', risk: 7 },
        { value: 'none', label: 'Keine zentrale Verwaltung', risk: 10 }
      ]
    },
    {
      id: 'monitoring',
      category: 'Überwachung',
      question: 'Werden Ihre IT-Systeme proaktiv überwacht (24/7 Monitoring)?',
      options: [
        { value: 'professional', label: 'Ja, professionelles 24/7-Monitoring', risk: 0 },
        { value: 'basic', label: 'Basis-Monitoring während Geschäftszeiten', risk: 5 },
        { value: 'reactive', label: 'Nur reaktiv bei Problemen', risk: 8 },
        { value: 'none', label: 'Kein Monitoring', risk: 10 }
      ]
    },
    {
      id: 'training',
      category: 'Mitarbeiter-Awareness',
      question: 'Wie oft erhalten Ihre Mitarbeiter Security-Awareness-Schulungen?',
      options: [
        { value: 'regular', label: 'Regelmäßig (mindestens jährlich)', risk: 0 },
        { value: 'occasional', label: 'Gelegentlich', risk: 4 },
        { value: 'onboarding', label: 'Nur beim Onboarding', risk: 7 },
        { value: 'never', label: 'Nie', risk: 10 }
      ]
    },
    {
      id: 'access',
      category: 'Zugriffsmanagement',
      question: 'Werden Zugriffsrechte regelmäßig überprüft und angepasst?',
      options: [
        { value: 'quarterly', label: 'Ja, quartalsweise oder häufiger', risk: 0 },
        { value: 'yearly', label: 'Jährlich', risk: 4 },
        { value: 'ondemand', label: 'Nur bei Bedarf', risk: 8 },
        { value: 'never', label: 'Selten bis nie', risk: 10 }
      ]
    }
  ];

  const handleAnswer = (questionId, option) => {
    setAnswers(prev => ({
      ...prev,
      [questionId]: option
    }));
  };

  const handleNext = () => {
    if (currentStep < questions.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      setShowResults(true);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 0) {
      setCurrentStep(prev => prev - 1);
    }
  };

  const handleRestart = () => {
    setAnswers({});
    setCurrentStep(0);
    setShowResults(false);
  };

  const calculateRiskScore = () => {
    let totalRisk = 0;
    let answeredQuestions = 0;

    questions.forEach(question => {
      const answer = answers[question.id];
      if (answer) {
        totalRisk += answer.risk;
        answeredQuestions++;
      }
    });

    const maxPossibleRisk = questions.length * 10;
    const riskPercentage = (totalRisk / maxPossibleRisk) * 100;

    return {
      totalRisk,
      riskPercentage: Math.round(riskPercentage),
      answeredQuestions,
      totalQuestions: questions.length
    };
  };

  const getRiskLevel = (percentage) => {
    if (percentage <= 25) return { level: 'Niedrig', color: '#10b981', icon: CheckCircle };
    if (percentage <= 50) return { level: 'Mittel', color: '#f59e0b', icon: AlertTriangle };
    if (percentage <= 75) return { level: 'Hoch', color: '#f97316', icon: AlertTriangle };
    return { level: 'Kritisch', color: '#ef4444', icon: XCircle };
  };

  const getRecommendations = (percentage) => {
    const recommendations = [];

    // Check each answer and provide specific recommendations
    if (answers.mfa?.value !== 'yes') {
      recommendations.push({
        priority: 'Hoch',
        title: 'Multi-Faktor-Authentifizierung implementieren',
        description: 'MFA reduziert das Risiko von Account-Kompromittierungen um über 99%.',
        action: 'Wir helfen Ihnen bei der Einführung von MFA-Lösungen wie Microsoft Authenticator oder Hardware-Token.'
      });
    }

    if (answers.backups?.value === 'never' || answers.backups?.value === 'yearly') {
      recommendations.push({
        priority: 'Kritisch',
        title: 'Backup-Testing-Strategie entwickeln',
        description: 'Ungetestete Backups sind im Ernstfall oft wertlos.',
        action: 'Lassen Sie uns Ihre Backup-Strategie überprüfen und ein Test-Konzept entwickeln.'
      });
    }

    if (answers.patches?.value === 'irregular' || answers.patches?.value === 'month') {
      recommendations.push({
        priority: 'Hoch',
        title: 'Patch-Management-Prozess optimieren',
        description: 'Verzögertes Patching ist eine der Hauptursachen für erfolgreiche Cyberangriffe.',
        action: 'Automatisieren Sie Ihr Patch-Management mit unserer Managed-Services-Lösung.'
      });
    }

    if (answers.drp?.value === 'no' || answers.drp?.value === 'partial') {
      recommendations.push({
        priority: 'Kritisch',
        title: 'Disaster Recovery Plan erstellen',
        description: 'Ohne getesteten DR-Plan riskieren Sie lange Ausfallzeiten bei Vorfällen.',
        action: 'Entwicklung und Test eines maßgeschneiderten Disaster Recovery Plans.'
      });
    }

    if (answers.monitoring?.value === 'none' || answers.monitoring?.value === 'reactive') {
      recommendations.push({
        priority: 'Hoch',
        title: '24/7-Monitoring implementieren',
        description: 'Proaktive Überwachung erkennt Probleme, bevor sie kritisch werden.',
        action: 'Unser SOC (Security Operations Center) überwacht Ihre Systeme rund um die Uhr.'
      });
    }

    if (answers.training?.value === 'never' || answers.training?.value === 'onboarding') {
      recommendations.push({
        priority: 'Mittel',
        title: 'Security-Awareness-Programm starten',
        description: 'Mitarbeiter sind oft das schwächste Glied in der Sicherheitskette.',
        action: 'Regelmäßige Schulungen und Phishing-Simulationen für Ihre Mitarbeiter.'
      });
    }

    // Add general recommendation if score is high
    if (percentage > 50 && recommendations.length === 0) {
      recommendations.push({
        priority: 'Mittel',
        title: 'Umfassende Security-Analyse empfohlen',
        description: 'Ihre Antworten zeigen Verbesserungspotenzial in mehreren Bereichen.',
        action: 'Kostenloser IT-Security-Check vor Ort mit detailliertem Bericht.'
      });
    }

    return recommendations.slice(0, 4); // Max 4 recommendations
  };

  const ResultsView = () => {
    const { riskPercentage } = calculateRiskScore();
    const risk = getRiskLevel(riskPercentage);
    const recommendations = getRecommendations(riskPercentage);
    const RiskIcon = risk.icon;

    return (
      <div style={{ padding: '20px' }}>
        {/* Risk Score Display */}
        <div style={{
          textAlign: 'center',
          marginBottom: '32px',
          padding: '32px',
          background: 'var(--bg-tertiary)',
          borderRadius: '16px',
          border: `3px solid ${risk.color}`
        }}>
          <RiskIcon size={64} color={risk.color} style={{ marginBottom: '16px' }} />
          <h3 style={{
            fontSize: '24px',
            fontWeight: '700',
            color: 'var(--text-primary)',
            marginBottom: '8px'
          }}>
            Ihr IT-Security-Risiko
          </h3>
          <div style={{
            fontSize: '48px',
            fontWeight: '800',
            color: risk.color,
            marginBottom: '8px'
          }}>
            {riskPercentage}%
          </div>
          <div style={{
            fontSize: '18px',
            fontWeight: '600',
            color: risk.color,
            textTransform: 'uppercase',
            letterSpacing: '1px'
          }}>
            Risiko-Level: {risk.level}
          </div>
        </div>

        {/* Risk Bar */}
        <div style={{ marginBottom: '32px' }}>
          <div style={{
            width: '100%',
            height: '12px',
            background: 'var(--bg-tertiary)',
            borderRadius: '6px',
            overflow: 'hidden',
            position: 'relative'
          }}>
            <div style={{
              width: `${riskPercentage}%`,
              height: '100%',
              background: risk.color,
              transition: 'width 1s ease-out'
            }} />
          </div>
          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            marginTop: '8px',
            fontSize: '12px',
            color: 'var(--text-tertiary)'
          }}>
            <span>Sicher</span>
            <span>Kritisch</span>
          </div>
        </div>

        {/* Interpretation */}
        <div style={{
          padding: '20px',
          background: 'var(--bg-tertiary)',
          borderRadius: '12px',
          marginBottom: '32px'
        }}>
          <h4 style={{ fontSize: '16px', fontWeight: '600', marginBottom: '12px', color: 'var(--text-primary)' }}>
            Was bedeutet das?
          </h4>
          <p style={{ fontSize: '14px', lineHeight: '1.6', color: 'var(--text-secondary)', margin: 0 }}>
            {riskPercentage <= 25 && 'Hervorragend! Ihre IT-Sicherheit ist gut aufgestellt. Kleinere Optimierungen können Ihren Schutz noch weiter verbessern.'}
            {riskPercentage > 25 && riskPercentage <= 50 && 'Ihre IT-Sicherheit hat ein solides Fundament, aber es gibt wichtige Bereiche, die Aufmerksamkeit benötigen.'}
            {riskPercentage > 50 && riskPercentage <= 75 && 'Achtung: Ihre IT-Infrastruktur weist erhebliche Sicherheitslücken auf. Handlungsbedarf ist gegeben.'}
            {riskPercentage > 75 && 'Kritisch: Ihre IT ist erheblichen Risiken ausgesetzt. Sofortiges Handeln ist dringend empfohlen, um Schäden zu vermeiden.'}
          </p>
        </div>

        {/* Recommendations */}
        {recommendations.length > 0 && (
          <div style={{ marginBottom: '32px' }}>
            <h4 style={{
              fontSize: '18px',
              fontWeight: '700',
              marginBottom: '16px',
              color: 'var(--text-primary)'
            }}>
              Unsere Empfehlungen für Sie
            </h4>
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
              {recommendations.map((rec, index) => (
                <div key={index} style={{
                  padding: '16px',
                  background: 'var(--bg-tertiary)',
                  borderRadius: '12px',
                  borderLeft: `4px solid ${
                    rec.priority === 'Kritisch' ? '#ef4444' :
                    rec.priority === 'Hoch' ? '#f97316' : '#f59e0b'
                  }`
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'start', marginBottom: '8px' }}>
                    <h5 style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', margin: 0 }}>
                      {rec.title}
                    </h5>
                    <span style={{
                      fontSize: '11px',
                      fontWeight: '600',
                      padding: '4px 8px',
                      borderRadius: '4px',
                      background: rec.priority === 'Kritisch' ? '#ef4444' :
                                 rec.priority === 'Hoch' ? '#f97316' : '#f59e0b',
                      color: 'white'
                    }}>
                      {rec.priority}
                    </span>
                  </div>
                  <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginBottom: '8px' }}>
                    {rec.description}
                  </p>
                  <p style={{ fontSize: '13px', color: '#0071e3', fontWeight: '500', margin: 0 }}>
                    ✓ {rec.action}
                  </p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
          <button
            onClick={() => {
              onClose();
              setTimeout(() => onOpenChat?.(), 300);
            }}
            style={{
              width: '100%',
              padding: '16px',
              background: 'linear-gradient(135deg, #0071e3, #00a8ff)',
              color: 'white',
              border: 'none',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px'
            }}
          >
            Mit KI-Experten besprechen
            <ArrowRight size={20} />
          </button>
          
          <a
            href="tel:+492241261310"
            style={{
              width: '100%',
              padding: '16px',
              background: 'var(--bg-tertiary)',
              color: 'var(--text-primary)',
              border: '2px solid var(--border-subtle)',
              borderRadius: '12px',
              fontSize: '16px',
              fontWeight: '600',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: '8px',
              textDecoration: 'none'
            }}
          >
            Sofort anrufen: +49 2241 261310
          </a>

          <button
            onClick={handleRestart}
            style={{
              width: '100%',
              padding: '12px',
              background: 'transparent',
              color: 'var(--text-secondary)',
              border: 'none',
              borderRadius: '8px',
              fontSize: '14px',
              fontWeight: '500',
              cursor: 'pointer'
            }}
          >
            Assessment neu starten
          </button>
        </div>
      </div>
    );
  };

  if (!isOpen) return null;

  const currentQuestion = questions[currentStep];
  const progress = ((currentStep + 1) / questions.length) * 100;
  const isAnswered = answers[currentQuestion?.id];

  return (
    <div style={{
      position: 'fixed',
      top: 0,
      left: 0,
      right: 0,
      bottom: 0,
      background: 'rgba(0, 0, 0, 0.7)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      zIndex: 999999,
      padding: '20px'
    }}>
      <div style={{
        background: 'var(--bg-secondary)',
        borderRadius: '20px',
        maxWidth: '600px',
        width: '100%',
        maxHeight: '90vh',
        overflow: 'auto',
        boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)'
      }}>
        {/* Header */}
        <div style={{
          padding: '24px',
          borderBottom: '1px solid var(--border-subtle)',
          position: 'sticky',
          top: 0,
          background: 'var(--bg-secondary)',
          zIndex: 10
        }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
              <Shield size={24} color="#0071e3" />
              <h2 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
                IT-Security-Risikocheck
              </h2>
            </div>
            <button
              onClick={onClose}
              style={{
                background: 'transparent',
                border: 'none',
                fontSize: '24px',
                cursor: 'pointer',
                color: 'var(--text-secondary)',
                padding: '4px'
              }}
            >
              ×
            </button>
          </div>
          
          {!showResults && (
            <>
              {/* Progress Bar */}
              <div style={{
                width: '100%',
                height: '8px',
                background: 'var(--bg-tertiary)',
                borderRadius: '4px',
                overflow: 'hidden',
                marginBottom: '8px'
              }}>
                <div style={{
                  width: `${progress}%`,
                  height: '100%',
                  background: 'linear-gradient(90deg, #0071e3, #00a8ff)',
                  transition: 'width 0.3s ease'
                }} />
              </div>
              <div style={{ fontSize: '12px', color: 'var(--text-tertiary)', textAlign: 'right' }}>
                Frage {currentStep + 1} von {questions.length}
              </div>
            </>
          )}
        </div>

        {/* Content */}
        {showResults ? (
          <ResultsView />
        ) : (
          <div style={{ padding: '24px' }}>
            {/* Category Badge */}
            <div style={{
              display: 'inline-block',
              padding: '6px 12px',
              background: 'var(--bg-tertiary)',
              borderRadius: '6px',
              fontSize: '12px',
              fontWeight: '600',
              color: '#0071e3',
              marginBottom: '16px'
            }}>
              {currentQuestion.category}
            </div>

            {/* Question */}
            <h3 style={{
              fontSize: '18px',
              fontWeight: '600',
              color: 'var(--text-primary)',
              marginBottom: '24px',
              lineHeight: '1.5'
            }}>
              {currentQuestion.question}
            </h3>

            {/* Options */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginBottom: '32px' }}>
              {currentQuestion.options.map((option) => (
                <button
                  key={option.value}
                  onClick={() => handleAnswer(currentQuestion.id, option)}
                  style={{
                    padding: '16px',
                    background: answers[currentQuestion.id]?.value === option.value 
                      ? 'var(--bg-tertiary)' 
                      : 'transparent',
                    border: answers[currentQuestion.id]?.value === option.value
                      ? '2px solid #0071e3'
                      : '2px solid var(--border-subtle)',
                    borderRadius: '12px',
                    fontSize: '14px',
                    fontWeight: '500',
                    color: 'var(--text-primary)',
                    cursor: 'pointer',
                    textAlign: 'left',
                    transition: 'all 0.2s ease'
                  }}
                >
                  {option.label}
                </button>
              ))}
            </div>

            {/* Navigation */}
            <div style={{ display: 'flex', gap: '12px' }}>
              <button
                onClick={handlePrevious}
                disabled={currentStep === 0}
                style={{
                  flex: 1,
                  padding: '14px',
                  background: 'var(--bg-tertiary)',
                  border: '2px solid var(--border-subtle)',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'var(--text-primary)',
                  cursor: currentStep === 0 ? 'not-allowed' : 'pointer',
                  opacity: currentStep === 0 ? 0.5 : 1,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                <ArrowLeft size={18} />
                Zurück
              </button>
              
              <button
                onClick={handleNext}
                disabled={!isAnswered}
                style={{
                  flex: 2,
                  padding: '14px',
                  background: isAnswered ? 'linear-gradient(135deg, #0071e3, #00a8ff)' : 'var(--bg-tertiary)',
                  border: 'none',
                  borderRadius: '12px',
                  fontSize: '15px',
                  fontWeight: '600',
                  color: 'white',
                  cursor: isAnswered ? 'pointer' : 'not-allowed',
                  opacity: isAnswered ? 1 : 0.5,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  gap: '8px'
                }}
              >
                {currentStep === questions.length - 1 ? 'Ergebnis anzeigen' : 'Weiter'}
                <ArrowRight size={18} />
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default SecurityRiskAssessment;
